import { useEffect, useState } from "react";

const BRAND_GREEN = "#244F30";
const BRAND_GOLD = "#C4922A";

const MESSAGES = [
  "Setting up your workspace…",
  "Almost there…",
  "Loading your dashboard…",
];

/**
 * Full-screen branded loader shown during the post-auth boot window.
 * Cycles through reassuring messages every ~3.5s and, after 30s, surfaces a
 * "taking longer than usual" note so the user knows we haven't stalled.
 */
export default function AuthLoadingScreen({
  message,
}: {
  message?: string;
}) {
  const [idx, setIdx] = useState(0);
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % MESSAGES.length), 3500);
    const slowT = setTimeout(() => setSlow(true), 30000);
    return () => {
      clearInterval(t);
      clearTimeout(slowT);
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center px-6"
      style={{ backgroundColor: "#ffffff" }}
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-8 max-w-sm text-center">
        <span
          className="font-heading text-3xl font-medium tracking-tight"
          style={{ color: BRAND_GREEN }}
        >
          oplif<span style={{ color: BRAND_GOLD }}>.</span>
        </span>

        <div className="relative w-12 h-12">
          <div
            className="absolute inset-0 rounded-full border-[3px] border-t-transparent animate-spin"
            style={{ borderColor: `${BRAND_GREEN}33`, borderTopColor: "transparent" }}
          />
          <div
            className="absolute inset-0 rounded-full border-[3px] border-transparent animate-spin"
            style={{ borderTopColor: BRAND_GOLD, animationDuration: "1.2s" }}
          />
        </div>

        <div className="min-h-[3rem] flex flex-col items-center gap-2">
          <p
            key={idx}
            className="text-base font-medium animate-in fade-in duration-500"
            style={{ color: BRAND_GREEN }}
          >
            {message ?? MESSAGES[idx]}
          </p>
          {slow && (
            <p
              className="text-sm animate-in fade-in duration-500"
              style={{ color: BRAND_GOLD }}
            >
              This is taking longer than usual — hang tight, we're almost done.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
